/**
 * Returns a localized display label for a given attribute internal name.
 * It looks up the column definition by attrInternalName and resolves a label
 * using the key "attrDisplayName{language}". If that key is missing, it falls
 * back to English ("attrDisplayNameen"), and finally to the attrInternalName.
 *
 * @param attrInternalName - The internal identifier of the attribute/field.
 * @param allColumnDefs - Array of column definitions containing display names.
 * @param language - Language code suffix used in the column definitions (e.g., "en", "de").
 * @returns Resolved display label string for the given attribute in the requested language.
 */
export const getDynamicLabel = (
  attrInternalName: string,
  allColumnDefs: Array<any>,
  language: string,
): string => {
  const col = allColumnDefs?.find((c) => c?.attrInternalName === attrInternalName);
  if (!col) return attrInternalName;
  const key = `attrDisplayName${language}`;
  return col[key] ?? col.attrDisplayNameen ?? attrInternalName;
};

/**
 * Truncates long option labels to keep ComboBox rendering compact and readable.
 *
 * Used in right form panel and bulk update flows to ensure large option text
 * does not break layout while preserving a predictable display length.
 *
 * @param label - Label text to render.
 * @param maxLength - Maximum number of visible characters before truncation. Defaults to 45.
 * @returns The original label when within limit, otherwise truncated text with `..` suffix.
 */
export const truncateLabel = (label?: string, maxLength = 45): string => {
  if (!label) return '';
  return label.length > maxLength ? `${label.slice(0, maxLength)}..` : label;
};

/**
 * Normalizes labels for stable, case-insensitive comparisons.
 */
export const normalizeLabel = (label: string | undefined): string =>
  (label ?? '').trim().toLowerCase();
